import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
  TrackByFunction,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { rxResource } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatSelectModule } from "@angular/material/select";
import { MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";

import { NotificationService } from "@app/shared/services/notification.service";

import { PaginatedApiResponse } from "@lib/types";
import { Document, ModuleType } from "@shared/models";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { DocumentsQueryParams } from "@shared/models/documents-query-params.dto";
import { ExpandableCardComponent } from "@shared/components/expandable-card/expandable-card.component";
import { LoadingSectionComponent } from "@shared/components/loading-section/loading-section.component";
import { SectionErrorComponent } from "@shared/components/section-error/section-error.component";
import { TableEmptyStateComponent } from "@shared/components/table-empty-state/table-empty-state.component";
import { UploadDocumentDialogComponent } from "@shared/components/upload-document-dialog/upload-document-dialog.component";
import {
  ConfirmationModalService,
  FileDownloadService,
} from "@shared/services";

import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from "@lib/constants/pagination";
import { translations } from "@app/i18n";
import {
  createPageEventHandler,
  createPaginationComputedSignals,
  createResourceStateSignals,
  createSortChangeHandler,
  setupDebouncedSearch,
} from "@lib/utils/list-page";
import { getErrorMessage } from "@app/lib/utils/error";

export type EntityType = "order" | "loan" | "event";

@Component({
  selector: "app-documents-table",
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    FormsModule,
    ExpandableCardComponent,
    LoadingSectionComponent,
    SectionErrorComponent,
    TableEmptyStateComponent,
  ],
  templateUrl: "./documents-table.component.html",
  styleUrl: "./documents-table.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentsTableComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly documentsApiService = inject(DocumentsApiService);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly notificationService = inject(NotificationService);
  private readonly confirmationModalService = inject(ConfirmationModalService);

  readonly translations = translations;

  readonly entityType = input.required<EntityType>();
  readonly entityId = input.required<number | null>();
  readonly orderId = input<number | null>(null);
  readonly documentTypeOptions = input<string[]>([]);
  readonly documentSourceOptions = input<string[]>([]);
  readonly documentUploadedByOptions = input<string[]>([]);
  readonly showUploadButton = input<boolean>(true);
  readonly title = input<string | undefined>(undefined);
  readonly emptyStateMessage = input<string | undefined>(undefined);

  readonly documentsDisplayedColumns = [
    "name",
    "id",
    "type",
    "source",
    "uploadedBy",
    "uploadedAt",
    "lastModified",
    "actions",
  ] as const;

  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  readonly documentsQueryParams = signal<DocumentsQueryParams>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    searchText: undefined,
    sortBy: "name",
    sortDirection: "desc",
    types: undefined,
    sources: undefined,
    uploadedBy: undefined,
  });

  readonly documentsSearchInput = signal<string>("");
  readonly documentsFiltersOpen = signal<boolean>(false);
  readonly selectedDocumentTypes = signal<string[]>([]);
  readonly selectedDocumentSources = signal<string[]>([]);
  readonly selectedDocumentUploadedBy = signal<string[]>([]);

  readonly documentsResource = rxResource<
    PaginatedApiResponse<Document>,
    {
      entityType: EntityType;
      entityId: number | null;
      orderId: number | null;
      queryParams: DocumentsQueryParams;
    }
  >({
    params: () => ({
      entityType: this.entityType(),
      entityId: this.entityId(),
      orderId: this.orderId(),
      queryParams: this.documentsQueryParams(),
    }),
    stream: ({ params }) => {
      if (!params.entityId) {
        throw new Error("Invalid entity ID");
      }

      switch (params.entityType) {
        case "order":
          return this.documentsApiService.getOrderDocuments(
            params.entityId,
            params.queryParams,
          );
        case "loan":
          return this.documentsApiService.getLoanDocuments(
            params.entityId,
            params.queryParams,
          );
        case "event":
          if (!params.orderId) {
            throw new Error("Order ID is required for event documents");
          }
          return this.documentsApiService.getOrderEventDocuments(
            params.orderId,
            params.entityId,
            params.queryParams,
          );
        default:
          throw new Error(`Unknown entity type: ${params.entityType}`);
      }
    },
  });

  private readonly documentsPagination = createPaginationComputedSignals(
    this.documentsResource,
  );
  private readonly documentsState = createResourceStateSignals(
    this.documentsResource,
  );

  readonly documents = this.documentsPagination.items;
  readonly totalDocumentsCount = this.documentsPagination.totalCount;
  readonly currentDocumentsPage = computed(() => {
    const pageNumber = this.documentsResource.value()?.pageNumber ?? 1;
    return pageNumber - 1;
  });
  readonly documentsPageSize = computed(
    () => this.documentsResource.value()?.pageSize ?? DEFAULT_PAGE_SIZE,
  );
  readonly isLoadingDocuments = this.documentsState.isLoading;
  readonly hasDocumentsError = this.documentsState.hasError;

  readonly handleDocumentsPageEvent = createPageEventHandler(
    this.documentsQueryParams,
  );
  readonly onDocumentsSortChange = createSortChangeHandler(
    this.documentsQueryParams,
  );

  constructor() {
    setupDebouncedSearch({
      searchInput: this.documentsSearchInput,
      queryParams: this.documentsQueryParams,
    });
  }

  readonly displayTitle = computed(() => {
    const customTitle = this.title();
    if (customTitle) {
      return customTitle;
    }
    return `Documents (${this.totalDocumentsCount()})`;
  });

  readonly emptyMessage = computed(() => {
    const customMessage = this.emptyStateMessage();
    if (customMessage) {
      return customMessage;
    }

    const searchText = this.documentsSearchInput();
    const hasActiveFilters =
      this.selectedDocumentTypes().length > 0 ||
      this.selectedDocumentSources().length > 0 ||
      this.selectedDocumentUploadedBy().length > 0;

    if (searchText || hasActiveFilters) {
      return "No documents match your search criteria. Try adjusting your filters or search terms.";
    }

    const entityType = this.entityType();
    switch (entityType) {
      case "order":
        return "There are no documents associated with this order yet.";
      case "loan":
        return "There are no documents associated with this loan yet.";
      case "event":
        return "There are no documents associated with this event yet.";
      default:
        return "No documents found.";
    }
  });

  readonly trackById: TrackByFunction<Document> = (_, doc) => doc.id;

  private readonly sortState = computed(() => {
    const params = this.documentsQueryParams();
    return {
      column: params.sortBy,
      direction: params.sortDirection,
    };
  });

  getSortIcon(column: string): string {
    const state = this.sortState();
    if (state.column !== column) {
      return "swap_vert";
    }
    return state.direction === "asc" ? "north" : "south";
  }

  onDocumentsSearchChange(value: string): void {
    this.documentsSearchInput.set(value);
  }

  toggleDocumentsFilters(): void {
    this.documentsFiltersOpen.set(!this.documentsFiltersOpen());
  }

  onDocumentTypeFilterChange(types: string[]): void {
    this.selectedDocumentTypes.set(types);
    this.documentsQueryParams.update((params) => ({
      ...params,
      types: types.length > 0 ? types : undefined,
      pageIndex: 0,
    }));
  }

  onDocumentSourceFilterChange(sources: string[]): void {
    this.selectedDocumentSources.set(sources);
    this.documentsQueryParams.update((params) => ({
      ...params,
      sources: sources.length > 0 ? sources : undefined,
      pageIndex: 0,
    }));
  }

  onDocumentUploadedByFilterChange(uploadedBy: string[]): void {
    this.selectedDocumentUploadedBy.set(uploadedBy);
    this.documentsQueryParams.update((params) => ({
      ...params,
      uploadedBy: uploadedBy.length > 0 ? uploadedBy : undefined,
      pageIndex: 0,
    }));
  }

  handleClearDocumentsFilters(): void {
    this.documentsSearchInput.set("");
    this.selectedDocumentTypes.set([]);
    this.selectedDocumentSources.set([]);
    this.selectedDocumentUploadedBy.set([]);
    this.documentsQueryParams.update((params) => ({
      ...params,
      searchText: undefined,
      types: undefined,
      sources: undefined,
      uploadedBy: undefined,
      pageIndex: 0,
    }));
  }

  handleUploadDocument(): void {
    const entityId = this.entityId();
    if (!entityId) {
      this.notificationService.error(
        "Cannot upload document. Please try again.",
      );
      return;
    }

    const entityType = this.entityType();
    let moduleType: ModuleType;
    let sourceId: number;
    let eventId: number | undefined;

    switch (entityType) {
      case "order":
        moduleType = ModuleType.ORDER;
        sourceId = entityId;
        break;
      case "loan":
        moduleType = ModuleType.LOAN;
        sourceId = entityId;
        break;
      case "event": {
        moduleType = ModuleType.EVENT;
        const orderIdValue = this.orderId();
        if (!orderIdValue) {
          this.notificationService.error(
            "Order ID is required for event documents. Please try again.",
          );
          return;
        }
        sourceId = orderIdValue;
        eventId = entityId;
        break;
      }
      default:
        this.notificationService.error("Invalid entity type");
        return;
    }

    const dialogRef = this.dialog.open(UploadDocumentDialogComponent, {
      width: "824px",
      disableClose: true,
      data: {
        ModuleType: moduleType,
        sourceId: sourceId,
        eventId: eventId,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result) {
          this.documentsResource.reload();
        }
      });
  }

  onRetryDocuments(): void {
    this.documentsResource.reload();
  }

  async onDownloadDocument(doc: Document): Promise<void> {
    const result = await this.fileDownloadService.downloadFile({
      url: doc.url,
      filename: doc.name,
    });

    if (!result.success) {
      this.notificationService.error(
        "Failed to download document. Please try again.",
      );
    }
  }

  onDeleteDocument(doc: Document): void {
    this.confirmationModalService
      .confirmDeleteDocument()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === "confirm") {
          this.deleteDocument(doc);
        }
      });
  }

  private deleteDocument(doc: Document): void {
    this.documentsApiService
      .deleteDocument(doc.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notificationService.success(
            `Document "${doc.name}" deleted successfully`,
          );
          this.documentsResource.reload();
        },
        error: (error) => {
          this.notificationService.error(
            getErrorMessage(
              error,
              "Failed to delete document. Please try again.",
            ),
          );
        },
      });
  }
}
